import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { get, post } from '../utils/api';
import type { CompletedLevelsMap, LevelProgress, SerializedLevelProgress } from '../types/course';
import { FUNDAMENTALS_COURSE, getAllLevelIds, FINAL_QUIZ_ID } from '../config/courseContent';

const STORAGE_KEY = 'wsle:courseProgress';

// ─── localStorage helpers ────────────────────────────────────
function readLocalProgress(): CompletedLevelsMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const arr: SerializedLevelProgress[] = JSON.parse(raw);
    const map: CompletedLevelsMap = new Map();
    arr.forEach((item) => map.set(item.levelId, { quizScore: item.quizScore, quizTotal: item.quizTotal }));
    return map;
  } catch {
    return new Map();
  }
}

function writeLocalProgress(map: CompletedLevelsMap): void {
  const arr: SerializedLevelProgress[] = [];
  map.forEach((val, key) => arr.push({ levelId: key, quizScore: val.quizScore, quizTotal: val.quizTotal }));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
}

function clearLocalProgress(): void {
  localStorage.removeItem(STORAGE_KEY);
}

// ─── Hook ────────────────────────────────────────────────────
export function useCourseProgress() {
  const { user } = useAuth();
  const [completedLevels, setCompletedLevels] = useState<CompletedLevelsMap>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  const allLevelIds = useMemo(() => getAllLevelIds(FUNDAMENTALS_COURSE), []);

  // Fetch progress on mount or user change
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);

      if (user) {
        try {
          const res = await get<{
            levels: Array<{ level_id: string; quiz_score: number | null; quiz_total: number | null }>;
          }>('/api/course/progress');
          if (!cancelled) {
            const map: CompletedLevelsMap = new Map();
            (res.data?.levels ?? []).forEach((l) =>
              map.set(l.level_id, { quizScore: l.quiz_score ?? 0, quizTotal: l.quiz_total ?? 0 })
            );
            setCompletedLevels(map);
          }
        } catch {
          // Fall back to localStorage even for signed-in users on error
          if (!cancelled) setCompletedLevels(readLocalProgress());
        }
      } else {
        // Guest: use localStorage
        if (!cancelled) setCompletedLevels(readLocalProgress());
      }

      if (!cancelled) setIsLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [user]);

  // Sync localStorage → backend on sign-in
  useEffect(() => {
    if (!user) return;
    const local = readLocalProgress();
    if (local.size === 0) return;

    const levels = Array.from(local.entries()).map(([levelId, val]) => {
      // Look up section for this level
      let sectionId = '';
      for (const section of FUNDAMENTALS_COURSE.sections) {
        if (section.levels.some((l) => l.id === levelId)) {
          sectionId = section.id;
          break;
        }
      }
      return { levelId, sectionId, quizScore: val.quizScore, quizTotal: val.quizTotal };
    });

    post('/api/course/progress/sync', { levels })
      .then(() => clearLocalProgress())
      .catch(() => {
        // Keep local data if sync fails
      });
  }, [user]);

  const isLevelUnlocked = useCallback(
    (levelId: string): boolean => {
      // Level 1 is always unlocked
      const idx = allLevelIds.indexOf(levelId);
      if (idx === 0) return true;

      // Final quiz: unlocked after last level
      if (levelId === FINAL_QUIZ_ID) {
        const lastLevelId = allLevelIds[allLevelIds.length - 1];
        return completedLevels.has(lastLevelId);
      }

      if (idx === -1) return false;

      // Previous level must be completed
      const prevId = allLevelIds[idx - 1];
      return completedLevels.has(prevId);
    },
    [allLevelIds, completedLevels]
  );

  const completeLevel = useCallback(
    async (levelId: string, sectionId: string, quizScore: number, quizTotal: number): Promise<void> => {
      const progress: LevelProgress = { quizScore, quizTotal };

      // Optimistic update
      setCompletedLevels((prev) => {
        const next = new Map(prev);
        next.set(levelId, progress);
        return next;
      });

      if (user) {
        try {
          await post('/api/course/progress', { levelId, sectionId, quizScore, quizTotal });
        } catch {
          // Progress already updated optimistically in state
        }
      } else {
        // Guest: save to localStorage
        const current = readLocalProgress();
        current.set(levelId, progress);
        writeLocalProgress(current);
      }
    },
    [user]
  );

  return { completedLevels, isLevelUnlocked, completeLevel, isLoading };
}
